import "shared/extensions/date";

import { ChangeEvent } from "react";
import { ChartFilters } from "../../../../UIPalette/components/Charts/ChartProps";
import { ClientDivisionSelector } from "../../../../UIPalette/components/ClientDivisionSelector/ClientDivisionSelector";
import { DivisionSelect } from "./DivisionSelect";
import { GroupingValues } from "../../../../UIPalette/components/Reports/ReportConstants";
import { Hideable } from "shared/UI/Hideable/Hideable";
import { LocaleSelect } from "shared/components/LocaleSelect";
import { RoleKeys } from "core/Constants";
import { TargetGroupTypeAhead } from "../../../../UIPalette/components/TargetGroupTypeAhead/TargetGroupTypeAhead";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { useTranslation } from "react-i18next";
import { Select } from "shared/UI/Select/Select";
import InputDatePicker from "shared/UI/DatePicker/InputDatePicker/InputDatePicker";

export type FilterProps = {
  filters: ChartFilters;
  dispatch: Function;
  hideAllFilters: boolean;
  hideDateFilters: boolean;
  hideLocaleFilter: boolean;
  hideGrouping: boolean;
};

export const ReportFilters = ({ filters, dispatch, hideAllFilters, hideDateFilters, hideLocaleFilter, hideGrouping }: FilterProps) => {
  const { t } = useTranslation();
  const { isAuthorised, user } = useAdminUserContext();
  const isHaAdmin = isAuthorised({ role: RoleKeys.HaAdmin });

  return (
    <Hideable hidden={hideAllFilters}>
      <div className="w-full mb-4 lg:w-3/4 xl:w-1/2">
        <Hideable hidden={hideDateFilters}>
          <div className="flex flex-row mb-3.5 space-x-3.5">
            <div className="relative basis-1/2">
              <InputDatePicker
                label={t("dashboard.filters.startDate")}
                date={Date.fromDateOnly(filters.dateFrom)}
                setDate={(date) => dispatch({ type: "SET_FROM_DATE", payload: date })}
                {...{
                  className: "[&>input]:bg-l-blue border-r-4 border-none [&>svg]:z-10 cursor-pointer leading-8",
                  name: "dateFrom",
                }}
              />
            </div>
            <div className="relative basis-1/2">
              <InputDatePicker
                minDate={Date.fromDateOnly(filters.dateFrom)}
                label={t("dashboard.filters.endDate")}
                date={Date.fromDateOnly(filters.dateTo)}
                setDate={(date) => dispatch({ type: "SET_TO_DATE", payload: date })}
                {...{
                  className: "[&>input]:bg-l-blue border-r-4 border-none [&>svg]:z-10 cursor-pointer leading-8",
                  name: "dateTo",
                }}
              />
            </div>
          </div>
        </Hideable>

        <Hideable hidden={isHaAdmin}>
          <div className="flex flex-row pr-3.5 mb-3.5">
            <DivisionSelect
              clientId={user?.client?.id!}
              onSelection={(division) => {
                dispatch({
                  type: "SET_DIVISION",
                  payload: division,
                });
              }}
              ariaLabel={t("dashboard.filters.ariaDivision")}
              value={filters.division?.id ?? ""}
              label={t("dashboard.filters.divisionPlaceholder")}
              placeholder={t("dashboard.filters.divisionPlaceholder")}
            />
          </div>
        </Hideable>

        <Hideable hidden={!isHaAdmin}>
          <div className="flex flex-row space-x-3.5">
            <div className="relative basis-2/3">
              <ClientDivisionSelector
                initialClient={filters.client}
                initialDivision={filters.division}
                onSelection={(clientAndDivision) =>
                  dispatch({
                    type: "SET_CLIENT_AND_DIVISION",
                    payload: clientAndDivision,
                  })
                }
                clientReadOnly={!isHaAdmin}
              />
            </div>
            <div className="relative basis-1/3">
              <TargetGroupTypeAhead
                onTargetGroupSelected={(targetGroup) =>
                  dispatch({
                    type: "SET_TARGET_GROUP",
                    payload: targetGroup,
                  })
                }
              />
            </div>
          </div>
        </Hideable>

        <div className="flex flex-row space-x-3.5 w-full">
          <Hideable hidden={hideGrouping}>
            <div className="relative w-full">
              <Select
                onChange={(grouping: ChangeEvent<HTMLSelectElement>) =>
                  dispatch({
                    type: "SET_GROUPING",
                    payload: grouping.target.value,
                  })
                }
                aria-label={t("dashboard.grouping.ariaDropdown")}
                name="groupingSelect"
                label={t("dashboard.grouping.title")}
                placeholder={t("dashboard.grouping.placeholder")}
                value={filters.groupBy}
              >
                <option value={GroupingValues.None}>{t("dashboard.grouping.noGrouping")}</option>
                <option value={GroupingValues.AgeRange}>{t("dashboard.grouping.ageRange")}</option>
                <option value={GroupingValues.Gender}>{t("dashboard.grouping.gender")}</option>
              </Select>
            </div>
          </Hideable>
          <Hideable hidden={hideLocaleFilter}>
            <LocaleSelect
              label={t("dashboard.filters.localeLabel")}
              value={filters.localeId}
              onChange={(locale: ChangeEvent<HTMLSelectElement>) =>
                dispatch({
                  type: "SET_LOCALE",
                  payload: locale.target.value,
                })
              }
              ariaLabel={t("dashboard.filters.ariaLocales")}
              allOption={true}
            />
          </Hideable>
        </div>
      </div>
    </Hideable>
  );
};
